import { Formik } from 'formik';
import { useCallback, useContext } from 'react';

import { vendorsApi } from 'src/app/services/Vendors/Vendors.service';
import {
  type IVendorProductLeadAddressTabFormValues,
  toVendorProductLeadAddressTabInitialValues,
  vendorProductLeadContactTabValidationSchema,
} from 'src/components/vendors/steps/Contact/VendorLeadStepAddressTab.validation';
import { VendorLeadStepAddressTabContent } from 'src/components/vendors/steps/Contact/VendorLeadStepAddressTabContent';
import { toApiAddress } from 'src/helpers/toApiAddress';
import { VendorsContext } from 'src/pages/VendorsPage/VendorsPage.context';
import type { IVendorLeadProduct } from 'src/types/vendors/IVendorLeadProduct';

type VendorLeadStepContactTabProps = {
  addressFieldName: Extract<keyof IVendorLeadProduct, 'shippingAddress' | 'billingAddress'>;
  vendorProductLeadId: string;

  onSubmit: () => boolean;
};

export function VendorLeadStepAddressTab({
  addressFieldName,
  vendorProductLeadId,
  onSubmit,
}: Readonly<VendorLeadStepContactTabProps>) {
  const { vendorLeadProduct, updateVendorLeadProduct } = useContext(VendorsContext);

  const [, partialUpdateVendorLeadProduct] = vendorsApi.usePartialUpdateVendorProductLeadMutation(vendorProductLeadId);

  const save = useCallback(
    async (newValues: IVendorProductLeadAddressTabFormValues, submit: boolean) => {
      const updatedCustomerProductLeadData = await partialUpdateVendorLeadProduct({
        data: {
          [addressFieldName]: {
            ...toApiAddress(newValues.address),

            submit,
          },
        },
      });

      updateVendorLeadProduct(updatedCustomerProductLeadData.data);
    },
    [partialUpdateVendorLeadProduct, addressFieldName, updateVendorLeadProduct]
  );

  const handleSubmit = useCallback(
    async (submitValues: IVendorProductLeadAddressTabFormValues) => {
      await save(submitValues, true);

      return onSubmit();
    },
    [save, onSubmit]
  );

  const handleChange = useCallback(
    (newValues: IVendorProductLeadAddressTabFormValues) => save(newValues, false),
    [save]
  );

  if (!vendorLeadProduct) {
    return null;
  }

  return (
    <Formik<IVendorProductLeadAddressTabFormValues>
      initialValues={toVendorProductLeadAddressTabInitialValues(vendorLeadProduct, addressFieldName)}
      validationSchema={vendorProductLeadContactTabValidationSchema}
      validateOnMount={true}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      {(formikProps) => <VendorLeadStepAddressTabContent formikProps={formikProps} onChange={handleChange} />}
    </Formik>
  );
}
